import Cookies from 'js-cookie'
import * as Lockr from 'lockr'
import { clearCookies, clearLocalStorage } from '../logout-utils'
import { onLogin } from '../login/on-login'
import { logNewRelicEvent } from '../common/custom/newrelic-page-events'
import { ErrorNRLogs } from '../network-adapter/error.tracking'
import { setSessionId } from '../login/get-session-id'
import { refreshTokenUrl } from '@/root/api/apiConstants'
import { HTTP_STATUS_CODE } from '@/root/constants/api-status-codes'
import { getHeaders } from '@/terminal/_utils/fetcher'
import { REFRESH_TOKEN_CONST } from '@/root/constants'
import { TRACE_ID_KEY } from '@/terminal/_constants/header'

export const handleLogout = (
  clearCookiesAndLS: boolean = true,
  source?: string,
) => {
  const headers = getHeaders()
  logNewRelicEvent('web_handle_logout', {
    source: source ?? 'unknown',
    traceId: headers[TRACE_ID_KEY] ?? '',
  })
  if (clearCookiesAndLS) {
    clearCookies()
    clearLocalStorage()
  }
  const pathname = window.location.pathname
  // List of paths where redirection should NOT happen
  const skipRedirectPaths = [
    /^\/blog/, // matches /blog, /blog/abc, /blog/article-123, etc.
    /^\/features/, // matches /features, /features-x, /features/abc, etc.
    /^\/mutual-funds\/[a-z0-9-]+-\d+$/, // matches /mutual-funds/abc-123, /mutual-funds/xyz-456, etc.
    /^\/calculators(\/.*)?$/, // matches /calculators and /calculators/abc, /calculators/xyz, etc.
    /^\/us-stocks\/[a-z0-9-]+-share-price-[a-z]+$/i, // matches /us-stocks/abc-share-price-xyz, /us-stocks/xyz-share-price-abc, etc.
  ]
  const shouldSkipRedirect = skipRedirectPaths.some(pattern =>
    pattern.test(pathname),
  )

  if (shouldSkipRedirect) {
    // No redirection for these paths
    return
  }

  window.location.href = `/login?return_to=${pathname.substring(
    0,
    1000,
  )}${window.location.search.substring(0, 1000)}`
}

export const handleRefreshToken = async (clearCookiesAndLS: boolean = true) => {
  const headers = getHeaders()
  try {
    const refreshToken = Cookies.get('refresh_token') ?? ''
    if (!refreshToken) {
      // if refresh_token is not present, then we need to logout the user
      handleLogout(
        clearCookiesAndLS,
        REFRESH_TOKEN_CONST.REFRESH_TOKEN_NOT_PRESENT,
      )
      return null
    }

    logNewRelicEvent('web_handle_refresh_token_called', {
      url: refreshTokenUrl,
      apiType: REFRESH_TOKEN_CONST.API_TYPE,
      traceId: headers[TRACE_ID_KEY] ?? '',
    })
    const refreshTokenResp = await fetch(refreshTokenUrl, {
      method: 'POST',
      headers: {
        ...headers,
        Authorization: `Bearer ${refreshToken}`,
      },
    })
    const refreshTokenRespJson = await refreshTokenResp.json()
    if (refreshTokenResp.status === HTTP_STATUS_CODE.SUCCESS) {
      logNewRelicEvent(ErrorNRLogs.webRefreshTokenSuccess, {
        statusCode: refreshTokenResp.status.toString(),
        url: refreshTokenUrl,
        apiType: REFRESH_TOKEN_CONST.API_TYPE,
        traceId: headers[TRACE_ID_KEY] ?? '',
      })
      // if API call is a success, then we need to set new Token in cookies and localStorage and verify new Encrypted Token on INDstocks (in onLogin())
      const newToken = refreshTokenRespJson.jwt_token
      const newEncryptedToken = refreshTokenRespJson.encrypted_token
      Lockr.set('token', newToken)
      Cookies.set('token', newToken, { secure: true })
      setSessionId()

      // this is to verify if the new token is set in the cookies and has replaced the old token
      const verifyToken = Cookies.get('token')
      if (!verifyToken || verifyToken !== newToken) {
        logNewRelicEvent(ErrorNRLogs.webRefreshTokenNotSet, {
          error: 'Failed to set new token in cookies or token mismatch',
          url: refreshTokenUrl,
          isTokenEmpty: !verifyToken?.length,
          traceId: headers[TRACE_ID_KEY] ?? '',
        })
      }
      onLogin(false, newEncryptedToken, window.location.pathname)
      return newToken
    } else if (refreshTokenResp.status === HTTP_STATUS_CODE.BAD_REQUEST) {
      logNewRelicEvent(ErrorNRLogs.webRefreshTokenBadRequestError, {
        error: JSON.stringify(refreshTokenRespJson),
        statusCode: refreshTokenResp.status.toString(),
        url: refreshTokenUrl,
        apiType: REFRESH_TOKEN_CONST.API_TYPE,
        traceId: headers[TRACE_ID_KEY] ?? '',
      })
      handleLogout(
        clearCookiesAndLS,
        REFRESH_TOKEN_CONST.REFRESH_TOKEN_BAD_REQUEST,
      )
    } else {
      logNewRelicEvent(ErrorNRLogs.webRefreshTokenError, {
        error: JSON.stringify(refreshTokenRespJson),
        statusCode: refreshTokenResp.status.toString(),
        url: refreshTokenUrl,
        apiType: REFRESH_TOKEN_CONST.API_TYPE,
        traceId: headers[TRACE_ID_KEY] ?? '',
      })
      handleLogout(clearCookiesAndLS, REFRESH_TOKEN_CONST.REFRESH_TOKEN_ERROR)
    }
  } catch (error: unknown) {
    logNewRelicEvent(ErrorNRLogs.webRefreshTokenError, {
      error: JSON.stringify(error),
      url: refreshTokenUrl,
      traceId: headers[TRACE_ID_KEY] ?? '',
    })
    handleLogout(clearCookiesAndLS)
  }
  return null
}
